import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getInternshipScoreByReqId, getScoreComparison } from '../api/crud';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Drawer,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactApexChart from 'react-apexcharts';
import InternStdDetail from '../Components/InternStdDetail';
import useMediaQuery from "@mui/material/useMediaQuery";

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
}));

const ScoreChip = styled(Chip)(({ score }) => {
  let color = 'default';
  let bgcolor = '#f5f5f5';
  
  if (score >= 4.5) {
    color = 'success';
    bgcolor = '#e8f5e8';
  } else if (score >= 4) {
    color = 'primary';
    bgcolor = '#e3f2fd';
  } else if (score >= 3) {
    color = 'warning';
    bgcolor = '#fff3e0';
  } else if (score >= 2) {
    color = 'error';
    bgcolor = '#ffebee';
  }
  
  return {
    backgroundColor: bgcolor,
    color: color === 'default' ? '#666' : undefined,
    fontWeight: 'medium',
  };
});

const InternshipEvaluationDisplay = ({ reqId }) => {
  const [evaluation, setEvaluation] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentSelected, setStudentSelected] = useState(null);
  const isMobile = useMediaQuery('(max-width:767px)');
  
  // State สำหรับการขยายข้อความ
  const [expandedSections, setExpandedSections] = useState({
    strengths: false,
    recommendations: false,
    required_skills: false,
    suggested_courses: false,
    collaboration_ideas: false
  });

  // ฟังก์ชันสำหรับ toggle การขยายข้อความ
  const toggleExpand = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // ฟังก์ชันสำหรับพิมพ์เป็น PDF
  const handlePrint = () => {
    // เพิ่ม CSS สำหรับการพิมพ์
    const printStyles = `
      <style>
        /* กำหนดขนาด A4 และ Paper Layout */
        body {
          margin: 0;
          padding: 0;
          background: #f5f5f5;
          font-family: 'TH Sarabun New', 'Sarabun', sans-serif;
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 20px 0;
        }
        
        .a4-paper {
          width: 210mm;
          min-height: 297mm;
          background: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          border: 1px solid #ddd;
          margin: 0 auto 20px auto;
          padding: 10mm;
          box-sizing: border-box;
          position: relative;
          font-size: 9pt;
          line-height: 1.2;
          color: #333;
        }
        
        .a4-paper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(to right, #e3f2fd, #2196f3, #e3f2fd);
        }

        @media print {
          body { 
            background: white !important;
            padding: 0 !important;
          }
          .a4-paper {
            width: 100% !important;
            min-height: auto !important;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 8mm !important;
            page-break-after: auto !important;
          }
          .a4-paper::before {
            display: none !important;
          }
          .preview-notice { 
            display: none !important; 
          }
          .page-break {
            page-break-before: always !important;
          }
        }
        
        .preview-notice { 
          margin-bottom: 20px; 
          text-align: center; 
          background: #e3f2fd; 
          padding: 10px; 
          border-radius: 5px; 
          border: 1px solid #2196f3;
        }
        
        .gradient-card {
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
          border: 2px solid #2196f3 !important;
          border-radius: 6px !important;
          padding: 8px !important;
          margin: 8px 0 !important;
        }
        
        .score-summary {
          text-align: center !important;
        }
        
        .evaluation-grid {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 4px !important;
        }
        
        .evaluation-item {
          border: 1px solid #ddd !important;
          padding: 4px !important;
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          border-radius: 2px !important;
          background: #fafafa !important;
        }
        
        .chip {
          background-color: #e3f2fd !important;
          color: #1976d2 !important;
          padding: 2px 4px !important;
          border-radius: 2px !important;
          font-size: 8pt !important;
          font-weight: bold !important;
        }
        
        h1, h2, h3, h4, h5, h6 { 
          color: #1976d2 !important; 
          margin-bottom: 4px !important;
          font-weight: bold !important;
        }
        
        h1 { font-size: 16pt !important; }
        h2 { font-size: 12pt !important; }
        h3 { font-size: 11pt !important; }
        h4 { font-size: 10pt !important; }
        
        .print-header {
          text-align: center;
          margin-bottom: 10px;
          border-bottom: 2px solid #2196f3;
          padding-bottom: 8px;
          background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 10px;
        }
        
        .print-header img {
          filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
        }
        
        .print-header h1 {
          margin-bottom: 2px !important;
        }
        
        .print-header h3 {
          margin-top: 0 !important;
          margin-bottom: 4px !important;
        }
        
        .print-date {
          text-align: right;
          font-size: 8pt;
          margin-bottom: 8px;
          color: #666;
        }
        
        .page-break {
          page-break-before: always;
          margin-top: 20px;
        }
        
        .student-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 10px;
          padding: 8px;
          background: #f8f9fa;
          border-radius: 4px;
          border: 1px solid #e9ecef;
        }
        
        .evaluator-info {
          background: #f0f8f0;
          border-left: 2px solid #4caf50;
          padding: 8px;
          border-radius: 4px;
        }
        
        .student-info {
          background: #f0f4ff;
          border-left: 2px solid #2196f3;
          padding: 8px;
          border-radius: 4px;
        }
        
        .info-section {
          margin-bottom: 10px;
          padding: 8px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          background: #fafafa;
        }
        
        .print-break {
          page-break-inside: avoid;
          margin-bottom: 10px;
        }
        
        .page-break {
          page-break-before: always;
        }
        
        .radar-chart-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 8px 0 0 0;
          padding: 8px;
          background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
          border-radius: 6px;
          border: 1px solid #bbdefb;
        }
        
        .radar-chart-wrapper {
          width: 250px;
          height: 250px;
          position: relative;
        }
      </style>
    `;

    // สร้างหน้าต่างใหม่สำหรับพิมพ์
    const printWindow = window.open('', '_blank', 'width=1200,height=1600,resizable=yes,scrollbars=yes');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>ผลการประเมินการฝึกงาน - REQ ID: ${evaluation.req_id}</title>
          <link href="https://fonts.googleapis.com/css2?family=TH+Sarabun+New:wght@300;400;500;600;700&family=Sarabun:wght@300;400;500;600;700&display=swap" rel="stylesheet">
          <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
          ${printStyles}
        </head>
        <body>
          <div class="a4-paper">
            <div class="print-date">วันที่พิมพ์: ${new Date().toLocaleDateString('th-TH', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
            weekday: 'long'
          })}</div>
          
          <div style="text-align: center; background: #e3f2fd; padding: 10px; margin-bottom: 20px; border-radius: 5px; border: 1px solid #2196f3;" class="preview-notice">
            <p style="margin: 0; color: #1976d2; font-weight: bold;">
              📄 ตัวอย่างรายงานการประเมิน - กดปุ่ม "พิมพ์เอกสาร" ที่มุมขวาบนเพื่อพิมพ์หรือบันทึกเป็น PDF
            </p>
          </div>
          
          <div class="print-header">
            <div style="text-align: center; margin-bottom: 8px;">
              <img src="${process.env.PUBLIC_URL}/assets/images/logo.jpeg" 
                   alt="โลโก้มหาวิทยาลัยบูรพา" 
                   style="width: 60px; height: 60px; object-fit: contain; margin-bottom: 8px;"
                   onerror="this.style.display='none';">
              <h1 style="margin: 0;">ผลการประเมินการฝึกงาน 2 เดือน</h1>
              <h3 style="margin: 2px 0;">คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา</h3>
            </div>
            <p style="text-align: center; margin: 4px 0 0 0;">REQ ID: ${evaluation.req_id}</p>
          </div>

          <div style="margin-bottom: 10px;">
            <h3>ข้อมูลนิสิตและผู้ประเมิน</h3>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 8px;">
              <div style="display: flex; gap: 8px; align-items: flex-start;">
                <div style="flex-shrink: 0; text-align: center; margin-right: 8px;">
                  <img src="${getStudentImageUrl(evaluation.req_id)}" 
                       alt="รูปนิสิต" 
                       style="width: 70px; height: 90px; object-fit: cover; border-radius: 4px; box-shadow: 0 1px 2px rgba(0,0,0,0.1);"
                       onerror="this.src='${process.env.PUBLIC_URL}/assets/images/logo.png';">
                </div>
                <div style="flex: 1;">
                  <h4 style="margin: 0 0 6px 0; color: #1976d2;">ข้อมูลนิสิต</h4>
                  <p style="margin: 0 0 4px 0; font-size: 10pt; font-weight: bold;">${evaluation.student_name}</p>
                  <p style="margin: 0 0 3px 0; font-size: 8pt;"><strong>รหัสนิสิต:</strong> ${getStudentId(evaluation.req_id)}</p>
                  <p style="margin: 0 0 3px 0; font-size: 8pt;"><strong>สาขาวิชา:</strong> ${evaluation.program}</p>
                  <p style="margin: 0; font-size: 8pt;"><strong>ระยะเวลาฝึกงาน:</strong> ${evaluation.period}</p>
                </div>
              </div>
              <div>
                <h4 style="margin: 0 0 6px 0; color: #2e7d32;">ข้อมูลผู้ประเมิน</h4>
                <p style="margin: 0 0 3px 0; font-size: 8pt;"><strong>ชื่อผู้ประเมิน:</strong> ${evaluation.evaluator_name}</p>
                <p style="margin: 0 0 3px 0; font-size: 8pt;"><strong>ตำแหน่ง:</strong> ${evaluation.evaluator_position}</p>
                <p style="margin: 0 0 3px 0; font-size: 8pt;"><strong>สถานประกอบการ:</strong> ${evaluation.company}</p>
                <p style="margin: 0 0 3px 0; font-size: 8pt;"><strong>อีเมล:</strong> ${evaluation.email || '-'}</p>
                <p style="margin: 0 0 3px 0; font-size: 8pt;"><strong>เบอร์โทรศัพท์:</strong> ${evaluation.evaluator_phone || '-'}</p>
                ${evaluation.timestamp_text ? `<p style="margin: 0; font-size: 8pt;"><strong>วันที่ประเมิน:</strong> ${evaluation.timestamp_text}</p>` : ''}
              </div>
            </div>
          </div>

          <div class="card gradient-card" style="margin-bottom: 8px; padding: 6px !important;">
            <div class="score-summary">
              <h3 style="margin: 0 0 4px 0; font-size: 10pt;">สรุปคะแนนรวม</h3>
              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; text-align: center;">
                <div>
                  <h2 style="margin: 0 0 1px 0; font-size: 11pt;">${evaluation.evaluation_15_total}</h2>
                  <p style="margin: 0; font-size: 7pt;">คะแนนการประเมิน 15 ด้าน<br>(เต็ม 75)</p>
                </div>
                <div>
                  <h2 style="margin: 0 0 1px 0; font-size: 11pt;">${evaluation.satisfaction_score}</h2>
                  <p style="margin: 0; font-size: 7pt;">ความพึงพอใจ<br>(เต็ม 10)</p>
                </div>
                <div>
                  <h1 style="margin: 0 0 1px 0; font-size: 13pt;">${evaluation.total_score_85}</h1>
                  <p style="margin: 0; font-size: 7pt;"><strong>คะแนนรวม (เต็ม 85)</strong></p>
                </div>
              </div>
              ${comparison ? `
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #ddd;">
                  <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; text-align: center; font-size: 7pt;">
                    <div><strong>ค่าเฉลี่ยรวม:</strong> ${comparison.overall_average}</div>
                    <div><strong>ค่าเฉลี่ยสาขา:</strong> ${comparison.program_average}</div>
                    <div><strong>Percentile:</strong> ${comparison.percentile}%</div>
                  </div>
                </div>
              ` : ''}
              
              <!-- Radar Chart -->
              <div class="radar-chart-container">
                <div class="radar-chart-wrapper">
                  <canvas id="radarChart"></canvas>
                </div>
              </div>
              
              <!-- ข้อมูลเพิ่มเติมจากผู้ประเมิน -->
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #ddd;">
                <h4 style="margin: 0 0 6px 0; color: #1976d2; font-size: 9pt; text-align: left;">ข้อมูลเพิ่มเติมจากผู้ประเมิน</h4>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; margin-bottom: 6px;">
                  <div style="border: 1px solid #e8f5e8; border-left: 2px solid #4caf50; padding: 4px; border-radius: 2px; background: #f8fff8;">
                    <h5 style="color: #2e7d32; margin: 0 0 2px 0; display: flex; align-items: center; gap: 2px; font-size: 8pt;">
                     
                      จุดเด่นของนิสิต
                    </h5>
                    <p style="margin: 0; line-height: 1.1; font-size: 7pt;">${evaluation.strengths || '<em style="color: #666;">ไม่มีข้อมูล</em>'}</p>
                  </div>

                  <div style="border: 1px solid #e3f2fd; border-left: 2px solid #2196f3; padding: 4px; border-radius: 2px; background: #f8fbff;">
                    <h5 style="color: #1976d2; margin: 0 0 2px 0; display: flex; align-items: center; gap: 2px; font-size: 8pt;">
                      
                      ข้อแนะนำสำหรับนิสิต
                    </h5>
                    <p style="margin: 0; line-height: 1.1; font-size: 7pt;">${evaluation.recommendations || '<em style="color: #666;">ไม่มีข้อมูล</em>'}</p>
                  </div>

                  <div style="border: 1px solid #fff3e0; border-left: 2px solid #ff9800; padding: 4px; border-radius: 2px; background: #fffcf8;">
                    <h5 style="color: #f57c00; margin: 0 0 2px 0; display: flex; align-items: center; gap: 2px; font-size: 8pt;">
                      
                      ทักษะที่ควรมีก่อนฝึกงาน
                    </h5>
                    <p style="margin: 0; line-height: 1.1; font-size: 7pt;">${evaluation.required_skills || '<em style="color: #666;">ไม่มีข้อมูล</em>'}</p>
                  </div>

                  <div style="border: 1px solid #f3e5f5; border-left: 2px solid #9c27b0; padding: 4px; border-radius: 2px; background: #fdfaff;">
                    <h5 style="color: #7b1fa2; margin: 0 0 2px 0; display: flex; align-items: center; gap: 2px; font-size: 8pt;">
                      
                      คอร์สอบรมที่แนะนำ
                    </h5>
                    <p style="margin: 0; line-height: 1.1; font-size: 7pt;">${evaluation.suggested_courses || '<em style="color: #666;">ไม่มีข้อมูล</em>'}</p>
                  </div>
                </div>

                <div style="border: 1px solid #e8f4fd; border-left: 2px solid #00acc1; padding: 4px; border-radius: 2px; background: linear-gradient(135deg, #f7fcff 0%, #e8f4fd 100%);">
                  <h5 style="color: #00838f; margin: 0 0 2px 0; display: flex; align-items: center; gap: 2px; font-size: 8pt;">
                   
                    ความร่วมมือกับคณะ/มหาวิทยาลัยที่อยากให้เกิด
                  </h5>
                  <p style="margin: 0; line-height: 1.1; font-size: 7pt;">${evaluation.collaboration_ideas || '<em style="color: #666;">ไม่มีข้อมูล</em>'}</p>
                </div>
              </div>
            </div>
          </div>

          <div style="margin-top: 8px; text-align: center; font-size: 7pt; color: #666;">
            <p style="margin: 0;">รายงานนี้จัดทำโดยระบบจัดการฝึกงาน คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา</p>
          </div>

          <!-- รายละเอียดการประเมิน 15 ด้าน -->
          <div class="page-break" style="margin-top: 20px;">
            <div class="print-header">
              <h1>รายละเอียดคะแนน 15 ด้าน (จัดกลุ่มตามทักษะ)</h1>
              <h3>คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา</h3>
              <p>REQ ID: ${evaluation.req_id} - ${evaluation.student_name}</p>
            </div>
            <div style="margin-bottom: 10px;">
              <h4>คะแนนเฉลี่ยแต่ละกลุ่มทักษะ:</h4>
              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; margin-bottom: 10px;">
                ${Object.entries(getEvaluationGroups()).map(([key, group]) => `
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px; border: 1px solid #ddd; border-radius: 2px; background: #f9f9f9; font-size: 8pt;">
                    <span style="font-weight: bold;">${group.name}:</span>
                    <span style="font-weight: bold; color: #1976d2;">${parseFloat(group.average).toFixed(2)}/5</span>
                  </div>
                `).join('')}
              </div>
            </div>
            
            ${Object.entries(getEvaluationGroups()).map(([groupKey, group]) => {
              const groupAspects = evaluationAspects.filter(aspect => aspect.group === groupKey);
              return `
                <div style="margin-bottom: 10px; page-break-inside: avoid;">
                  <h4 style="color: #1976d2; border-bottom: 1px solid #1976d2; padding-bottom: 2px; margin-bottom: 4px; font-size: 9pt;">
                    ${group.name} (เฉลี่ย: ${parseFloat(group.average).toFixed(2)}/5)
                  </h4>
                  <div class="evaluation-grid">
                    ${groupAspects.map(aspect => {
                      const score = evaluation[aspect.key];
                      return `
                        <div class="evaluation-item">
                          <span style="flex: 1; font-size: 8pt;">${aspect.label}</span>
                          <span class="chip">${getScoreText(score)} (${score}/5)</span>
                        </div>
                      `;
                    }).join('')}
                  </div>
                </div>
              `;
            }).join('')}
          </div>

          <div style="margin-top: 8px; text-align: center; font-size: 7pt; color: #666;">
            <p style="margin: 0;">รายงานนี้จัดทำโดยระบบจัดการฝึกงาน คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา</p>
          </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // ไม่เรียก print() ทันที ให้ผู้ใช้ดูเนื้อหาก่อน
    printWindow.focus();
    
    // เพิ่มปุ่มพิมพ์ในหน้าต่างใหม่
    printWindow.addEventListener('load', () => {
      // ล็อคสัดส่วนหน้าต่างตามอัตราส่วน A4 (210:297)
      const lockAspectRatio = () => {
        const aspectRatio = 210 / 297; // อัตราส่วน A4
        const minWidth = 800; // ความกว้างขั้นต่ำ
        const maxWidth = 1400; // ความกว้างสูงสุด
        
        let currentWidth = printWindow.outerWidth;
        
        // จำกัดขนาดหน้าต่าง
        if (currentWidth < minWidth) currentWidth = minWidth;
        if (currentWidth > maxWidth) currentWidth = maxWidth;
        
        const expectedHeight = currentWidth / aspectRatio + 120; // +120 สำหรับ toolbar
        
        printWindow.resizeTo(currentWidth, expectedHeight);
      };
      
      // ตั้งค่าเริ่มต้น
      lockAspectRatio();
      
      // ติดตามการเปลี่ยนขนาดหน้าต่าง
      printWindow.addEventListener('resize', lockAspectRatio);
      
      // ปุ่มพิมพ์
      const printButton = printWindow.document.createElement('button');
      printButton.innerHTML = '🖨️ พิมพ์เอกสาร';
      printButton.style.cssText = `
        position: fixed;
        top: 10px;
        right: 120px;
        z-index: 9999;
        background: #007bff;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
      `;
      printButton.onclick = () => {
        printButton.style.display = 'none';
        closeButton.style.display = 'none';
        printWindow.print();
      };
      
      // ปุ่มปิดหน้าต่าง
      const closeButton = printWindow.document.createElement('button');
      closeButton.innerHTML = '❌ ปิด';
      closeButton.style.cssText = `
        position: fixed;
        top: 10px;
        right: 10px;
        z-index: 9999;
        background: #dc3545;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
      `;
      closeButton.onclick = () => {
        printWindow.close();
      };
      
      printWindow.document.body.appendChild(printButton);
      printWindow.document.body.appendChild(closeButton);
      
      // สร้าง Radar Chart
      setTimeout(() => {
        const ctx = printWindow.document.getElementById('radarChart');
        if (ctx) {
          const groupData = getEvaluationGroups();
          const chartData = {
            labels: Object.values(groupData).map(group => group.name),
            datasets: [{
              label: 'คะแนนการประเมิน',
              data: Object.values(groupData).map(group => parseFloat(group.average)),
              backgroundColor: 'rgba(33, 150, 243, 0.2)',
              borderColor: 'rgba(33, 150, 243, 1)',
              borderWidth: 2,
              pointBackgroundColor: 'rgba(33, 150, 243, 1)',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              pointRadius: 5
            }]
          };
          
          new printWindow.Chart(ctx, {
            type: 'radar',
            data: chartData,
            options: {
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  display: false
                }
              },
              scales: {
                r: {
                  beginAtZero: true,
                  max: 5,
                  min: 0,
                  ticks: {
                    stepSize: 1,
                    font: {
                      family: 'TH Sarabun New',
                      size: 10
                    }
                  },
                  pointLabels: {
                    font: {
                      family: 'TH Sarabun New',
                      size: 9
                    },
                    color: '#1976d2'
                  },
                  grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                  },
                  angleLines: {
                    color: 'rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }
          });
        }
      }, 500);
    });
  };

  useEffect(() => {
    const fetchEvaluationData = async () => {
      if (!reqId) return;
      
      setLoading(true);
      try {
        const [evalData, comparisonData] = await Promise.all([
          getInternshipScoreByReqId(reqId),
          getScoreComparison(reqId)
        ]);
        
        setEvaluation(evalData);
        setComparison(comparisonData);
      } catch (err) {
        setError('ไม่สามารถโหลดข้อมูลการประเมินได้');
        console.error('Error fetching evaluation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluationData();
  }, [reqId]);

  // ฟังก์ชันแปลงคะแนนเป็นข้อความ
  const getScoreText = (score) => {
    if (score >= 4.5) return 'ดีมาก';
    if (score >= 4) return 'ดี';
    if (score >= 3) return 'พอใช้';
    if (score >= 2) return 'น้อย';
    return 'ควรปรับปรุง';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>กำลังโหลดข้อมูล...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mx: 'auto', maxWidth: 600 }}>
        {error}
      </Alert>
    );
  }

  if (!evaluation) {
    return (
      <Alert severity="info" sx={{ mx: 'auto', maxWidth: 600 }}>
        ไม่พบข้อมูลการประเมินสำหรับ req_id: {reqId}
      </Alert>
    );
  }

  const evaluationAspects = [
    { key: 'work_effectiveness_score', label: 'ประสิทธิผลของงานที่ได้รับมอบหมาย', group: 'performance' },
    { key: 'initiative_score', label: 'การริเริ่ม การประยุกต์และการค้นหาองค์ความรู้เพิ่มเติม', group: 'skills' },
    { key: 'communication_score', label: 'ความสามารถในการสื่อสารและการนำเสนอ', group: 'communication' },
    { key: 'learning_score', label: 'ความสนใจในการเรียนรู้งานและทักษะในการแก้ปัญหา', group: 'skills' },
    { key: 'compliance_score', label: 'การปฏิบัติตัวตามกฎระเบียบขององค์กร', group: 'behavior' },
    { key: 'responsibility_score', label: 'ความรับผิดชอบและการตรงต่อเวลา', group: 'behavior' },
    { key: 'leadership_score', label: 'ความกล้าแสดงความคิดเห็นและภาวะผู้นำ', group: 'leadership' },
    { key: 'relationship_score', label: 'มนุษยสัมพันธ์ในการทำงาน', group: 'communication' },
    { key: 'teamwork_score', label: 'ความสามารถในการทำงานเป็นทีม', group: 'communication' },
    { key: 'confidence_score', label: 'เชื่อมั่นในความสามารถตนเอง', group: 'leadership' },
    { key: 'consideration_score', label: 'การเห็นแก่ประโยชน์ผู้อื่น', group: 'behavior' },
    { key: 'commitment_score', label: 'มุ่งมั่นและพยายามในการเรียนรู้', group: 'attitude' },
    { key: 'enthusiasm_score', label: 'กระตือรือร้นในการเรียนรู้สิ่งใหม่ๆ', group: 'attitude' },
    { key: 'improvement_score', label: 'ปรับปรุงพัฒนาตนเองอยู่เสมอ', group: 'attitude' },
    { key: 'respect_score', label: 'มีความเคารพ อ่อนน้อม', group: 'behavior' }
  ];

  // ฟังก์ชันจัดกลุ่มการประเมิน
  const getEvaluationGroups = () => {
    const groups = {
      performance: { name: 'ประสิทธิภาพการทำงาน', scores: [], color: '#FF6B6B' },
      skills: { name: 'ทักษะและการเรียนรู้', scores: [], color: '#4ECDC4' },
      communication: { name: 'การสื่อสารและทำงานร่วมกัน', scores: [], color: '#45B7D1' },
      leadership: { name: 'ภาวะผู้นำและความมั่นใจ', scores: [], color: '#96CEB4' },
      behavior: { name: 'พฤติกรรมและจริยธรรม', scores: [], color: '#FFEAA7' },
      attitude: { name: 'เจตคติและการพัฒนาตนเอง', scores: [], color: '#DDA0DD' }
    };

    // ฟังก์ชันสำหรับแปลงคะแนนให้ปลอดภัย
    const normalizeScore = (rawScore) => {
      if (rawScore === null || rawScore === undefined || rawScore === '') {
        return null;
      }
      
      // ลองแปลงเป็นตัวเลข
      const numScore = parseFloat(rawScore);
      
      // ตรวจสอบว่าเป็นตัวเลขที่ถูกต้องและอยู่ในช่วง 1-5
      if (isNaN(numScore) || numScore < 1 || numScore > 5) {
        return null;
      }
      
      return numScore;
    };

    evaluationAspects.forEach(aspect => {
      const rawScore = evaluation ? evaluation[aspect.key] : null;
      const normalizedScore = normalizeScore(rawScore);
      
      if (normalizedScore !== null && groups[aspect.group]) {
        groups[aspect.group].scores.push(normalizedScore);
      }
    });

    // คำนวณค่าเฉลี่ยของแต่ละกลุ่ม
    Object.keys(groups).forEach(key => {
      const validScores = groups[key].scores.filter(score => score !== null && !isNaN(score));
      const average = validScores.length > 0 
        ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length
        : 0;
      
      groups[key].average = Math.round(average * 100) / 100; // ปัดเศษให้เหลือ 2 ตำแหน่ง
    });

    return groups;
  };

  // สร้างข้อมูลสำหรับ Radar Chart
  const getRadarChartData = () => {
    const groups = getEvaluationGroups();
    const categories = Object.keys(groups).map(key => groups[key].name);
    const series = [{
      name: 'คะแนนเฉลี่ย',
      data: Object.keys(groups).map(key => parseFloat(groups[key].average))
    }];

    return { categories, series };
  };

  // ฟังก์ชันสำหรับแยกรหัสนิสิต 8 ตัวแรกจาก req_id
  const getStudentId = (reqId) => {
    return reqId ? reqId.toString().substring(0, 8) : '';
  };

  // ฟังก์ชันสำหรับสร้าง URL รูปนิสิต
  const getStudentImageUrl = (reqId) => {
    const studentId = getStudentId(reqId);
    console.log('Fetching student image for ID:', studentId);
    return studentId ? `https://reg.buu.ac.th/registrar/getstudentimage.asp?id=${studentId}` : `${process.env.PUBLIC_URL}/assets/images/logo.png`;
  };

  // ฟังก์ชันสร้างข้อมูลกราฟ Bell Curve (ใช้ mean/std จริงจาก comparison ถ้ามี)
  const getBellCurveData = () => {
    // ใช้ mean/std จาก comparison ถ้ามี ไม่เช่นนั้น fallback เป็น 50/15
    const mean = comparison?.overall_average ? parseFloat(comparison.overall_average) : 50;
    // ลองใช้ std จาก comparison ถ้ามี (เช่น comparison.std_score หรือ comparison.std)
    const std = comparison?.std_score ? parseFloat(comparison.std_score) : (comparison?.std ? parseFloat(comparison.std) : 15);
    const bell = [];
    for (let x = 0; x <= 100; x += 1) {
      const y = (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - mean) / std, 2));
      bell.push({ x, y });
    }
    return bell;
  };

  return (
    <>
      {/* Drawer สำหรับแสดงรายละเอียดนิสิต */}
      <Drawer
        anchor="right"
        open={studentSelected ? true : false}
        onClose={() => {
          setStudentSelected(null);
        }}
      >
        <div
          className="card-body shadow-lg"
          style={{ width: "100vw", maxWidth: "600px" }}
        >
          <div className="row g-0">
            <div className="col-12 mb-4">
              <Box display="flex" justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setStudentSelected(null);
                  }}
                  startIcon={<CloseIcon />}
                >
                  {isMobile ? 'Close' : ''}
                </Button>
              </Box>

              <InternStdDetail internRequestId={studentSelected?.req_id} />

              <Divider />
              {/* add back button */}
              {isMobile && (
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    onClick={() => {
                      setStudentSelected(null);
                    }}
                    startIcon={<ArrowBackIcon />}
                  >
                    {isMobile ? 'BACK' : ''}
                  </Button>
                </Box>
              )}
            </div>
          </div>
          <hr />
        </div>
      </Drawer>

      <style jsx global>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 12pt !important; }
        }
      `}</style>
      <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box 
        mb={4} 
        sx={{
          background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
          borderRadius: 3,
          p: 4,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(33, 150, 243, 0.4)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="7" cy="7" r="1"/%3E%3Ccircle cx="27" cy="7" r="1"/%3E%3Ccircle cx="47" cy="7" r="1"/%3E%3Ccircle cx="7" cy="27" r="1"/%3E%3Ccircle cx="27" cy="27" r="1"/%3E%3Ccircle cx="47" cy="27" r="1"/%3E%3Ccircle cx="7" cy="47" r="1"/%3E%3Ccircle cx="27" cy="47" r="1"/%3E%3Ccircle cx="47" cy="47" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            pointerEvents: 'none'
          }
        }}
      >
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="flex-start"
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  mb: 1,
                  color: 'white',
                  textAlign: 'left'
                }}
              >
                ผลการประเมินการฝึกงาน 2 เดือน
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'medium',
                  fontSize: '1.1rem',
                  mb: 2,
                  color: 'rgba(255, 255, 255, 0.9)',
                  textAlign: 'left'
                }}
              >
                คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <Typography 
                variant="subtitle1" 
                onClick={() => setStudentSelected(evaluation)}
                role="button"
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    transform: 'scale(1.02)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                REQ ID: {evaluation.req_id}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="พิมพ์รายงานเป็น PDF" arrow>
              <IconButton 
                onClick={handlePrint}
                size="large"
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': { 
                    background: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  },
                  transition: 'all 0.3s ease',
                  width: 56,
                  height: 56
                }}
                className="no-print"
              >
                <PrintIcon sx={{ fontSize: '24px' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            opacity: 0.5
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            opacity: 0.7
          }}
        />
      </Box>

      {/* ข้อมูลพื้นฐาน */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent sx={{ bgcolor: 'primary.50' }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                ข้อมูลนิสิต
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    {evaluation.student_name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        รหัสนิสิต:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {getStudentId(evaluation.req_id)}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        สาขาวิชา:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {evaluation.program}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        ระยะเวลาฝึกงาน:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {evaluation.period}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ flexShrink: 0, ml: 2, mr: 1 }}>
                 
                  <img
                    src={getStudentImageUrl(evaluation.req_id)}
                    alt="รูปนิสิต"
                    onClick={() => setStudentSelected(evaluation)}
                    role="button"
                    style={{
                      width: '120px',
                      height: '150px',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.02)';
                      e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                    }}
                    onError={(e) => {
                      e.target.src = '/assets/images/logo.png';
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent sx={{ bgcolor: 'success.50', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="success.main">
                ข้อมูลผู้ประเมิน
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {evaluation.evaluator_name}
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        ตำแหน่ง:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                        {evaluation.evaluator_position}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        อีเมล:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                        {evaluation.email || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        เบอร์โทรศัพท์:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                        {evaluation.evaluator_phone || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {evaluation.timestamp_text && (
                    <Grid item xs={6}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          วันที่ประเมิน:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                          {evaluation.timestamp_text}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    สถานประกอบการ:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}>
                    {evaluation.company}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* สรุปคะแนนรวม และ Radar Chart */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* ส่วนซ้าย: สรุปคะแนนรวม */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2.5,
                
                borderRadius: 2,
               
                height: '100%'
              }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  color: 'primary.main', 
                  fontWeight: 'bold',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1.1rem',
                  
                }}>
                  สรุปคะแนนรวม
                </Typography>
                
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box 
                      sx={{ 
                        textAlign: 'center',
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: '2px solid #ff9800',
                        boxShadow: '0 3px 10px rgba(255, 152, 0, 0.2)',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          bgcolor: '#ff9800',
                          borderRadius: '4px 4px 0 0'
                        }
                      }}
                    >
                      <Typography variant="h2" sx={{ color: '#ff9800', fontWeight: 'bold', mb: 0.5, fontSize: '2.8rem' }}>
                        {evaluation.total_score_85}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800', fontSize: '1rem', lineHeight: 1.2 }}>
                        คะแนนรวม<br/><span style={{ fontSize: '0.85rem', opacity: 0.8, fontWeight: 'normal' }}>(เต็ม 85)</span>
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        textAlign: 'center',
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: '2px solid #2196f3',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          bgcolor: '#2196f3',
                          borderRadius: '4px 4px 0 0'
                        }
                      }}
                    >
                      <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold', mb: 0.5, fontSize: '1.8rem' }}>
                        {evaluation.evaluation_15_total}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem', lineHeight: 1.2 }}>
                        การประเมิน 15 ด้าน<br/><span style={{ fontSize: '0.7rem', opacity: 0.8 }}>(เต็ม 75)</span>
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        textAlign: 'center',
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: '2px solid #4caf50',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          bgcolor: '#4caf50',
                          borderRadius: '4px 4px 0 0'
                        }
                      }}
                    >
                      <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold', mb: 0.5, fontSize: '1.8rem' }}>
                        {evaluation.satisfaction_score}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem', lineHeight: 1.2 }}>
                        ความพึงพอใจ<br/><span style={{ fontSize: '0.7rem', opacity: 0.8 }}>(เต็ม 10)</span>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {comparison && (
                  <Box mt={2} pt={1.5} sx={{ borderTop: '1px solid #e0e0e0' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Box sx={{ 
                          textAlign: 'center',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>
                            ค่าเฉลี่ยรวม
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '0.9rem' }}>
                            {comparison.overall_average}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ 
                          textAlign: 'center',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>
                            ค่าเฉลี่ยสาขา
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '0.9rem' }}>
                            {comparison.program_average}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ 
                          textAlign: 'center',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>
                            Percentile
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '0.9rem' }}>
                            {comparison.percentile}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {/* Bell Curve Graph */}
                    <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto', mt: 2 }}>
                      <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', mb: 1 }}>
                        การกระจาย Percentile ของนิสิตฝึกงานทั้งหมด
                      </Typography>
                      <ReactApexChart
                        type="line"
                        height={180}
                        series={[{
                          name: 'Distribution',
                          data: getBellCurveData().map(p => [p.x, p.y])
                        }]}
                        options={{
                          chart: {
                            id: 'bell-curve',
                            toolbar: { show: false },
                            zoom: { enabled: false }
                          },
                          xaxis: {
                            min: 0,
                            max: 100,
                            tickAmount: 10,
                            title: { text: 'Percentile' },
                            labels: { formatter: v => `${v}` }
                          },
                          yaxis: {
                            show: false
                          },
                          stroke: { width: 3, curve: 'smooth' },
                          markers: {
                            size: 0
                          },
                          annotations: {
                            xaxis: [
                              comparison?.percentile !== undefined ? {
                                x: parseFloat(comparison.percentile),
                                borderColor: '#FF5722',
                                label: {
                                  borderColor: '#FF5722',
                                  style: { color: '#fff', background: '#FF5722' },
                                  orientation: 'vertical',
                                  text: `นิสิต (${comparison.percentile}%)`
                                }
                              } : null
                            ].filter(Boolean)
                          },
                          tooltip: {
                            enabled: false
                          }
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* ส่วนขวา: Radar Chart */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '100%', minHeight: '450px' }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  color: 'primary.main', 
                  fontWeight: 'bold',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1.1rem'
                }}>
                  การแสดงผลการประเมินตามกลุ่มทักษะ
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                  แสดงค่าเฉลี่ยคะแนนแยกตามกลุ่มทักษะ 6 ด้าน
                </Typography>
                
                <Box sx={{ height: '320px', mb: 2 }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'radar',
                        toolbar: {
                          show: false
                        }
                      },
                      xaxis: {
                        categories: getRadarChartData().categories,
                        labels: {
                          style: {
                            fontSize: '11px',
                            fontFamily: 'Sarabun, sans-serif'
                          }
                        }
                      },
                      yaxis: {
                        show: true,
                        min: 0,
                        max: 5,
                        tickAmount: 5,
                        labels: {
                          formatter: function (val) {
                            return val.toFixed(1);
                          }
                        }
                      },
                      plotOptions: {
                        radar: {
                          size: 120,
                          polygons: {
                            strokeColors: '#e9e9e9',
                            fill: {
                              colors: ['#f8f9fa', '#ffffff']
                            }
                          }
                        }
                      },
                      colors: ['#667eea'],
                      markers: {
                        size: 5,
                        colors: ['#667eea'],
                        strokeColor: '#fff',
                        strokeWidth: 2
                      },
                      fill: {
                        opacity: 0.1
                      },
                      stroke: {
                        width: 2
                      },
                      legend: {
                        show: false
                      },
                      title: {
                        text: 'ผลการประเมินตามกลุ่มทักษะ',
                        align: 'center',
                        style: {
                          fontSize: '14px',
                          fontFamily: 'Sarabun, sans-serif',
                          fontWeight: 600
                        }
                      }
                    }}
                    series={getRadarChartData().series}
                    type="radar"
                    height={320}
                  />
                </Box>
                
                {/* คะแนนเฉลี่ยแต่ละกลุ่ม - แบบกะทัดรัด */}
                <Box sx={{ mt: 1 }}>
                  <Grid container spacing={0.5}>
                    {Object.entries(getEvaluationGroups()).map(([key, group]) => (
                      <Grid item xs={6} key={key}>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 0.5,
                            px: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            bgcolor: `${group.color}15`,
                            fontSize: '0.8rem'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box 
                              sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                bgcolor: group.color 
                              }} 
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                              {group.name}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: group.color, fontSize: '0.75rem' }}>
                            {parseFloat(group.average).toFixed(1)}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* การประเมิน 15 ด้าน - แยกตามกลุ่ม */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ผลการประเมิน 15 ด้าน (จัดกลุ่มตามทักษะ)
          </Typography>
          
          {Object.entries(getEvaluationGroups()).map(([groupKey, group]) => {
            const groupAspects = evaluationAspects.filter(aspect => aspect.group === groupKey);
            
            return (
              <Box key={groupKey} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box 
                    sx={{ 
                      width: 16, 
                      height: 16, 
                      borderRadius: '50%', 
                      bgcolor: group.color 
                    }} 
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: group.color }}>
                    {group.name} (เฉลี่ย: {group.average}/5)
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {groupAspects.map((aspect, index) => {
                    const score = evaluation[aspect.key];
                    return (
                      <Grid item xs={12} md={6} key={index}>
                        <Box 
                          sx={{ 
                            p: 2, 
                            border: '1px solid #e0e0e0', 
                            borderLeft: `4px solid ${group.color}`,
                            borderRadius: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            bgcolor: `${group.color}10`
                          }}
                        >
                          <Typography variant="body2" sx={{ flex: 1, mr: 2 }}>
                            {aspect.label}
                          </Typography>
                          <ScoreChip 
                            label={`${getScoreText(score)} (${score}/5)`}
                            score={score}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}
        </CardContent>
      </StyledCard>

      {/* ข้อมูลเพิ่มเติม */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
            ข้อมูลเพิ่มเติมจากผู้ประเมิน
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  p: 3, 
                  border: '1px solid #e8f5e8',
                  borderLeft: '4px solid #4caf50',
                  borderRadius: 2,
                  bgcolor: '#f8fff8',
                  height: '100%',
                  minHeight: '180px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    จุดเด่นของนิสิต
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.6,
                      maxHeight: evaluation.strengths && evaluation.strengths.length > 200 && !expandedSections.strengths ? '120px' : 'auto',
                      overflow: evaluation.strengths && evaluation.strengths.length > 200 && !expandedSections.strengths ? 'hidden' : 'visible',
                      display: evaluation.strengths && evaluation.strengths.length > 200 && !expandedSections.strengths ? '-webkit-box' : 'block',
                      WebkitLineClamp: evaluation.strengths && evaluation.strengths.length > 200 && !expandedSections.strengths ? 5 : 'none',
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {evaluation.strengths || (
                      <span style="color: #666; font-style: italic;">
                        ไม่มีข้อมูล
                      </span>
                    )}
                  </Typography>
                  {evaluation.strengths && evaluation.strengths.length > 200 && (
                    <Box 
                      sx={{ 
                        mt: 1,
                        textAlign: 'right'
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#2e7d32', 
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => toggleExpand('strengths')}
                      >
                        {expandedSections.strengths ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  p: 3, 
                  border: '1px solid #e3f2fd',
                  borderLeft: '4px solid #2196f3',
                  borderRadius: 2,
                  bgcolor: '#f8fbff',
                  height: '100%',
                  minHeight: '180px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    ข้อแนะนำสำหรับนิสิต
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.6,
                      maxHeight: evaluation.recommendations && evaluation.recommendations.length > 200 && !expandedSections.recommendations ? '120px' : 'auto',
                      overflow: evaluation.recommendations && evaluation.recommendations.length > 200 && !expandedSections.recommendations ? 'hidden' : 'visible',
                      display: evaluation.recommendations && evaluation.recommendations.length > 200 && !expandedSections.recommendations ? '-webkit-box' : 'block',
                      WebkitLineClamp: evaluation.recommendations && evaluation.recommendations.length > 200 && !expandedSections.recommendations ? 5 : 'none',
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {evaluation.recommendations || (
                      <span style="color: #666; font-style: italic;">
                        ไม่มีข้อมูล
                      </span>
                    )}
                  </Typography>
                  {evaluation.recommendations && evaluation.recommendations.length > 200 && (
                    <Box 
                      sx={{ 
                        mt: 1,
                        textAlign: 'right'
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#1976d2', 
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => toggleExpand('recommendations')}
                      >
                        {expandedSections.recommendations ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  p: 3, 
                  border: '1px solid #fff3e0',
                  borderLeft: '4px solid #ff9800',
                  borderRadius: 2,
                  bgcolor: '#fffcf8',
                  height: '100%',
                  minHeight: '180px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                    ความรู้/ทักษะที่ควรมีก่อนฝึกงาน
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.6,
                      maxHeight: evaluation.required_skills && evaluation.required_skills.length > 200 && !expandedSections.required_skills ? '120px' : 'auto',
                      overflow: evaluation.required_skills && evaluation.required_skills.length >  200 && !expandedSections.required_skills ? 'hidden' : 'visible',
                      display: evaluation.required_skills && evaluation.required_skills.length > 200 && !expandedSections.required_skills ? '-webkit-box' : 'block',
                      WebkitLineClamp: evaluation.required_skills && evaluation.required_skills.length > 200 && !expandedSections.required_skills ? 5 : 'none',
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {evaluation.required_skills || (
                      <span style="color: #666; font-style: italic;">
                        ไม่มีข้อมูล
                      </span>
                    )}
                  </Typography>
                  {evaluation.required_skills && evaluation.required_skills.length > 200 && (
                    <Box 
                      sx={{ 
                        mt: 1,
                        textAlign: 'right'
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#f57c00', 
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => toggleExpand('required_skills')}
                      >
                        {expandedSections.required_skills ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  p: 3, 
                  border: '1px solid #f3e5f5',
                  borderLeft: '4px solid #9c27b0',
                  borderRadius: 2,
                  bgcolor: '#fdfaff',
                  height: '100%',
                  minHeight: '180px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                 
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                    คอร์สอบรมที่แนะนำ
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.6,
                      maxHeight: evaluation.suggested_courses && evaluation.suggested_courses.length > 200 && !expandedSections.suggested_courses ? '120px' : 'auto',
                      overflow: evaluation.suggested_courses && evaluation.suggested_courses.length > 200 && !expandedSections.suggested_courses ? 'hidden' : 'visible',
                      display: evaluation.suggested_courses && evaluation.suggested_courses.length > 200 && !expandedSections.suggested_courses ? '-webkit-box' : 'block',
                      WebkitLineClamp: evaluation.suggested_courses && evaluation.suggested_courses.length > 200 && !expandedSections.suggested_courses ? 5 : 'none',
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {evaluation.suggested_courses || (
                      <span style="color: #666; font-style: italic;">
                        ไม่มีข้อมูล
                      </span>
                    )}
                  </Typography>
                  {evaluation.suggested_courses && evaluation.suggested_courses.length > 200 && (
                    <Box 
                      sx={{ 
                        mt: 1,
                        textAlign: 'right'
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#7b1fa2', 
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => toggleExpand('suggested_courses')}
                      >
                        {expandedSections.suggested_courses ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box 
                sx={{ 
                  p: 3, 
                  border: '1px solid #e8f4fd',
                  borderLeft: '4px solid #00acc1',
                  borderRadius: 2,
                  bgcolor: '#f7fcff',
                  background: 'linear-gradient(135deg, #f7fcff 0%, #e8f4fd 100%)',
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
               
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#00838f' }}>
                    ความร่วมมือกับคณะ/มหาวิทยาลัยที่อยากให้เกิด
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.6,
                      maxHeight: evaluation.collaboration_ideas && evaluation.collaboration_ideas.length > 300 && !expandedSections.collaboration_ideas ? '100px' : 'auto',
                      overflow: evaluation.collaboration_ideas && evaluation.collaboration_ideas.length > 300 && !expandedSections.collaboration_ideas ? 'hidden' : 'visible',
                      display: evaluation.collaboration_ideas && evaluation.collaboration_ideas.length > 300 && !expandedSections.collaboration_ideas ? '-webkit-box' : 'block',
                      WebkitLineClamp: evaluation.collaboration_ideas && evaluation.collaboration_ideas.length > 300 && !expandedSections.collaboration_ideas ? 4 : 'none',
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {evaluation.collaboration_ideas || (
                      <span style="color: #666; font-style: italic;">
                        ไม่มีข้อมูล
                      </span>
                    )}
                  </Typography>
                  {evaluation.collaboration_ideas && evaluation.collaboration_ideas.length > 300 && (
                    <Box 
                      sx={{ 
                        mt: 1,
                        textAlign: 'right'
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#00838f', 
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => toggleExpand('collaboration_ideas')}
                      >
                        {expandedSections.collaboration_ideas ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      
    </Container>
    </>
  );
};

// Component สำหรับใช้กับ Router
const InternshipEvaluationPage = () => {
  const [searchParams] = useSearchParams();
  const reqIdFromUrl = searchParams.get('req_id');
  const [reqId, setReqId] = useState(reqIdFromUrl || '');
  const [showResults, setShowResults] = useState(!!reqIdFromUrl);

  useEffect(() => {
    if (reqIdFromUrl) {
      setReqId(reqIdFromUrl);
      setShowResults(true);
    }
  }, [reqIdFromUrl]);

  const handleSearch = () => {
    if (reqId.trim()) {
      setShowResults(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Search Section */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              ค้นหาผลการประเมินฝึกงาน
            </Typography>
            <Box display="flex" gap={2} alignItems="end">
              <TextField
                fullWidth
                label="REQ ID"
                value={reqId}
                onChange={(e) => setReqId(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="กรอก REQ ID เช่น 6405021601"
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ minWidth: '120px', height: '56px' }}
              >
                ค้นหา
              </Button>
            </Box>
          </CardContent>
        </StyledCard>

        {/* Results */}
        {showResults && reqId && (
          <>
            <InternshipEvaluationDisplay reqId={reqId} />
          </>
        )}
      </Container>
    </Box>
  );
};

export default InternshipEvaluationPage;
